
if (!customElements.get('search-filter')) {
    class SearchFilter extends HTMLElement {
        constructor() {
            super();
            this.filterRequestController = null;
            this.filterRequestNumber = 0;
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            if (this.filterRequestController) this.filterRequestController.abort();
        }

        async init() {
            this.elements = {
                filterDetails: this.querySelectorAll('details'),
                filterSummary: this.querySelectorAll('summary'),
                loadingSpinner: this.querySelector('.loading__spinner')
            };

            // Keep the original names even after a selection changes the summary text.
            this.elements.filterDetails.forEach(detail => {
                const span = detail.querySelector('summary > span');
                if (span) detail.dataset.originalFilterLabel = span.textContent.trim();
            });

            try {
                await this.initialFilterRendering();
            } catch (error) {
                console.error('Vehicle filter initial load failed:', error);
                this.removeAttribute('data-loading');
                if (this.elements.loadingSpinner) this.elements.loadingSpinner.classList.add('hidden');
            }

            this.elements.filterItems = this.querySelectorAll('.filter-select__item:not(.holder)');
            this.elements.filterSubmitButton = this.querySelector('.filter-value');
            if (!this.elements.filterSubmitButton) return;

            if (!this.elements.filterSubmitButton.hasAttribute('data-previous-href')) {
                this.elements.filterSubmitButton.setAttribute(
                    'data-previous-href',
                    this.elements.filterSubmitButton.getAttribute('href') || '/collections/all'
                );
            }
            this.previousHref = this.elements.filterSubmitButton.getAttribute('data-previous-href');

            this.sortYearOptions();

            this.elements.filterDetails.forEach(detail => {
                detail.addEventListener('click', this.closeDetails.bind(this, detail));
                const summary = detail.querySelector('summary');
                if (summary) {
                    summary.setAttribute('tabindex', '0');
                    summary.addEventListener('keydown', this.handleKeyDown.bind(this, detail));
                    detail.addEventListener('focusout', this.handleFocusOut.bind(this, detail));
                }
            });

            this.boundCloseAllDetails = this.closeAllDetails.bind(this);
            document.addEventListener('click', this.boundCloseAllDetails);

            this.elements.filterItems.forEach(item => {
                item.addEventListener('click', this.onClick.bind(this, item));
                item.setAttribute('tabindex', '0');
                item.setAttribute('role', 'button');
                item.addEventListener('keydown', this.handleItemKeyDown.bind(this, item));
            });

            // Start with the normal full lists. After a selection, narrow the
            // later dropdowns using Shopify's actual facet response.
            this.setSearchHref();
        }

        sortYearOptions() {
            this.elements.filterDetails.forEach(detail => {
                const filterSelect = detail.querySelector('.filter-select');
                if (!filterSelect || (detail.dataset.originalFilterLabel || '').toLowerCase() !== 'year') return;
                const items = Array.from(filterSelect.querySelectorAll('.filter-select__item:not(.holder)'));
                items.sort((a, b) => Number(b.textContent.trim()) - Number(a.textContent.trim()));
                items.forEach(item => filterSelect.appendChild(item));
            });
        }

        handleKeyDown(details, event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                details.open = !details.open;
            }
        }

        handleItemKeyDown(item, event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.onClick(item);
            }
        }

        closeDetails(clickedDetail) {
            this.elements.filterDetails.forEach(detail => {
                if (detail !== clickedDetail) detail.removeAttribute('open');
            });
        }

        closeAllDetails(event) {
            if (!Array.from(this.elements.filterDetails).some(detail => detail.contains(event.target))) {
                this.elements.filterDetails.forEach(detail => detail.removeAttribute('open'));
            }
        }

        handleFocusOut(detail) {
            setTimeout(() => {
                if (!detail.contains(document.activeElement)) detail.removeAttribute('open');
            }, 0);
        }

        resetDetail(detail) {
            detail.querySelectorAll('.filter-select__item.active:not(.holder)').forEach(item => {
                item.classList.remove('active');
            });
            const span = detail.querySelector('summary > span');
            if (span) span.textContent = detail.dataset.originalFilterLabel || '';
        }

        onClick(item) {
            if (item.hidden || item.classList.contains('disabled') || item.getAttribute('aria-disabled') === 'true') return;
            const detail = item.closest('details');
            if (!detail) return;
            const details = Array.from(this.elements.filterDetails);
            const changedIndex = details.indexOf(detail);
            const wasActive = item.classList.contains('active');

            this.resetDetail(detail);
            if (!wasActive) {
                item.classList.add('active');
                const span = detail.querySelector('summary > span');
                if (span) span.textContent = item.textContent.trim();
            }
            detail.removeAttribute('open');

            // A new year invalidates the old make/model; a new make invalidates the old model.
            for (let i = changedIndex + 1; i < details.length; i++) this.resetDetail(details[i]);

            // Restore all options while Shopify calculates the new allowed values.
            for (let i = changedIndex + 1; i < details.length; i++) {
                details[i].querySelectorAll('.filter-select__item:not(.holder)').forEach(option => {
                    option.hidden = false;
                    option.classList.remove('disabled');
                    option.removeAttribute('aria-disabled');
                    option.setAttribute('tabindex', '0');
                });
            }
            this.updateFilters();
        }

        getActiveParameters() {
            const params = new URLSearchParams();
            this.querySelectorAll('.filter-select__item.active:not(.holder)').forEach(item => {
                const filter = item.getAttribute('data-filter');
                if (!filter) return;
                const equalsIndex = filter.indexOf('=');
                if (equalsIndex < 0) return;
                params.append(
                    decodeURIComponent(filter.substring(0, equalsIndex)),
                    decodeURIComponent(filter.substring(equalsIndex + 1).replace(/\+/g, '%20'))
                );
            });
            return params;
        }

        buildFilterUrl() {
            const url = new URL(this.previousHref || '/collections/all', window.location.origin);
            const params = this.getActiveParameters();
            params.forEach((value, key) => url.searchParams.append(key, value));
            return url.toString();
        }

        setSearchHref() {
            const url = this.buildFilterUrl();
            this.elements.filterSubmitButton.setAttribute('href', url);
            this.elements.filterSubmitButton.removeAttribute('aria-disabled');
            return url;
        }

        // Compare the options currently displayed with Shopify's filtered facet values.
        // Shopify doesn't necessarily mark absent values as disabled, so presence matters.
        async updateFilters() {
            const url = this.setSearchHref();
            const requestNumber = ++this.filterRequestNumber;
            if (this.filterRequestController) this.filterRequestController.abort();
            this.filterRequestController = new AbortController();

            const details = Array.from(this.elements.filterDetails);
            const activeIndices = details.map((detail, index) =>
                detail.querySelector('.filter-select__item.active:not(.holder)') ? index : -1
            ).filter(index => index >= 0);
            if (activeIndices.length === 0) {
                details.forEach(detail => this.showAllOptions(detail));
                return;
            }
            const lastActiveIndex = Math.max(...activeIndices);

            try {
                const response = await fetch(url, { signal: this.filterRequestController.signal });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const html = await response.text();
                if (requestNumber !== this.filterRequestNumber) return;
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const form = doc.querySelector('facet-filters-form');
                if (!form) throw new Error('Shopify facet-filters-form not found');

                // Shopify's filter values are marked with data-filter in this theme.
                // Disabled/zero-count values must not be considered available.
                const available = new Set();
                form.querySelectorAll('[data-filter]').forEach(node => {
                    const filter = node.getAttribute('data-filter');
                    if (!filter || !filter.includes('=')) return;
                    const input = node.matches('input') ? node : node.querySelector('input');
                    const disabled = node.matches('[disabled], [aria-disabled="true"]') ||
                        (input && (input.disabled || input.getAttribute('aria-disabled') === 'true'));
                    if (!disabled) available.add(this.normalizeFilter(filter));
                });

                details.forEach((detail, index) => {
                    if (index <= lastActiveIndex) return;
                    detail.querySelectorAll('.filter-select__item:not(.holder)').forEach(item => {
                        const filter = this.normalizeFilter(item.getAttribute('data-filter') || '');
                        const show = available.has(filter);
                        item.hidden = !show;
                        item.classList.toggle('disabled', !show);
                        item.setAttribute('aria-disabled', String(!show));
                        item.setAttribute('tabindex', show ? '0' : '-1');
                    });
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Vehicle filter update failed:', error);
                    // Don't leave a misleading partial list if the request fails.
                    details.forEach((detail, index) => {
                        if (index > lastActiveIndex) this.showAllOptions(detail);
                    });
                }
            }
        }

        normalizeFilter(filter) {
            const index = filter.indexOf('=');
            if (index < 0) return filter;
            try {
                return decodeURIComponent(filter.slice(0, index)) + '=' +
                    decodeURIComponent(filter.slice(index + 1).replace(/\+/g, '%20'));
            } catch (_) {
                return filter;
            }
        }

        showAllOptions(detail) {
            detail.querySelectorAll('.filter-select__item:not(.holder)').forEach(item => {
                item.hidden = false;
                item.classList.remove('disabled');
                item.removeAttribute('aria-disabled');
                item.setAttribute('tabindex', '0');
            });
        }

        async initialFilterRendering() {
            if (this.dataset.collection !== 'all') {
                if (this.elements.loadingSpinner) this.elements.loadingSpinner.classList.remove('hidden');
                this.setAttribute('data-loading', 'true');
                try {
                    await this.loadFilters(this.dataset.filterUrl);
                } finally {
                    this.removeAttribute('data-loading');
                    if (this.elements.loadingSpinner) this.elements.loadingSpinner.classList.add('hidden');
                }
            }
        }

        async loadFilters(url) {
            if (!this.elements.filterDetails || !url) return;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const form = doc.querySelector('facet-filters-form');
            if (!form) return;

            let facets = form.querySelectorAll('.facets__summary');
            let labelSelector = '.facets__summary-label';
            if (!facets.length) {
                facets = form.querySelectorAll('.mobile-facets__summary');
                labelSelector = 'div > span';
            }

            this.elements.filterDetails.forEach(detail => {
                const select = detail.querySelector('.filter-select');
                const label = (detail.dataset.originalFilterLabel || '').toLowerCase();
                if (!select || !label) return;

                facets.forEach(facet => {
                    const facetLabel = facet.querySelector(labelSelector);
                    if (!facetLabel || !facetLabel.textContent.trim().toLowerCase().includes(label)) return;
                    const values = facet.parentElement.querySelectorAll('[data-filter-value-name]');
                    const options = new Map();
                    values.forEach(value => {
                        const name = value.dataset.filterValueName;
                        const filter = value.dataset.filter;
                        if (name && filter && !options.has(name)) options.set(name, { name, filter });
                    });
                    const sorted = Array.from(options.values());
                    if (label === 'year') sorted.sort((a, b) => Number(b.name) - Number(a.name));
                    sorted.forEach(option => select.appendChild(this.createFilterHtml(option.filter, option.name)));
                });
            });
        }

        createFilterHtml(filterId, label) {
            const li = document.createElement('li');
            li.className = 'filter-select__item';
            li.setAttribute('tabindex', '0');
            li.setAttribute('role', 'button');
            li.setAttribute('data-filter', filterId);
            li.textContent = label;
            return li;
        }
    }

    customElements.define('search-filter', SearchFilter);
}
